/**
 * The edit dialogs of the user data editor: a run's time and covered distance, a step record's
 * count, a trail's distance, a walk's time and distance.
 *
 * Each is a UserDataActionDialog with its inputs pre-filled from the row, so the admin corrects a
 * figure rather than retyping it. Only fields that actually changed are sent — the server leaves
 * an omitted field alone — so an edit to the time does not also write back a rounded distance.
 *
 * Mount them only while editing (keyed by the row), so the inputs start from the row every time.
 */

import { useState } from 'react'
import { Stack, TextField, Typography } from '@mui/material'
import type {
  AdminEditSessionRequest,
  AdminEditStepEventRequest,
  AdminEditTrailRequest,
  AdminEditWalkRequest,
  AdminUserSessionDto,
  AdminUserStepEventDto,
  AdminUserTrailDto,
  AdminUserWalkDto,
} from '@api/types/userData'
import { UserDataActionDialog } from './UserDataActionDialog'
import { formatDate, formatDistance, formatDuration } from './userFormatting'

interface EditDialogProps<TRow, TRequest> {
  row: TRow
  pending: boolean
  error: string | null
  onSubmit: (request: TRequest) => void
  onClose: () => void
}

/** The server's bound on one step record. */
export const MAX_STEP_EVENT_STEPS = 200000

// ---- Parsing ----
// Inputs are kept as strings so a half-typed value is not coerced. Each parser returns null for a
// blank input and NaN for one that is not acceptable.

function parseWhole(value: string): number {
  return /^\d+$/.test(value.trim()) ? Number(value.trim()) : NaN
}

function parseMeters(value: string): number | null {
  const text = value.trim()
  if (text === '') return null
  const n = Number(text)
  return Number.isFinite(n) && n >= 0 ? n : NaN
}

function splitSeconds(total?: number | null): string[] {
  if (total == null) return ['', '', '']
  const secs = Math.max(0, Math.round(total))
  return [String(Math.floor(secs / 3600)), String(Math.floor((secs % 3600) / 60)), String(secs % 60)]
}

/** Total seconds from hours/minutes/seconds inputs; a blank part counts as zero. */
function joinSeconds(parts: string[]): number | null {
  if (parts.every((p) => p.trim() === '')) return null
  const [h, m, s] = parts.map((p) => (p.trim() === '' ? 0 : parseWhole(p)))
  return h * 3600 + m * 60 + s
}

/** Metres as an editable string: one decimal is more precision than any of these figures have. */
function metersText(meters?: number | null): string {
  return meters == null ? '' : String(Math.round(meters * 10) / 10)
}

/** Whether an entered distance differs from the stored one by more than the input's rounding. */
function metersChanged(entered: number | null, stored?: number | null): boolean {
  return entered !== null && (stored == null || Number.isNaN(entered) || Math.abs(entered - stored) >= 0.05)
}

// ---- Inputs ----

function DurationFields({ value, onChange, label }: { value: string[]; onChange: (v: string[]) => void; label: string }) {
  const seconds = joinSeconds(value)

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        {label}
        {seconds !== null && !Number.isNaN(seconds) && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {formatDuration(seconds)}
          </Typography>
        )}
      </Typography>
      <Stack direction="row" spacing={1}>
        {['Hours', 'Minutes', 'Seconds'].map((part, i) => (
          <TextField
            key={part}
            label={part}
            type="number"
            size="small"
            value={value[i]}
            onChange={(e) => onChange(value.map((v, j) => (j === i ? e.target.value : v)))}
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />
        ))}
      </Stack>
    </>
  )
}

function MetersField({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const meters = parseMeters(value)

  return (
    <TextField
      label={`${label} (metres)`}
      type="number"
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: 0, step: 'any' }}
      helperText={meters !== null && !Number.isNaN(meters) ? formatDistance(meters) : 'In metres'}
      fullWidth
      sx={{ mt: 2 }}
    />
  )
}

// ---- Dialogs ----

export function EditRunDialog({
  row,
  pending,
  error,
  onSubmit,
  onClose,
}: EditDialogProps<AdminUserSessionDto, AdminEditSessionRequest>) {
  const [time, setTime] = useState(() => splitSeconds(row.durationSeconds))
  const [covered, setCovered] = useState(() => metersText(row.coveredMeters))

  const seconds = joinSeconds(time)
  const meters = parseMeters(covered)
  const timeChanged = seconds !== null && seconds !== row.durationSeconds
  const coveredChanged = metersChanged(meters, row.coveredMeters)

  const validate = () => {
    if (seconds !== null && (Number.isNaN(seconds) || seconds <= 0))
      return 'The time must be whole hours, minutes and seconds, and more than zero.'
    if (Number.isNaN(meters)) return 'The covered distance must be a number of metres, zero or more.'
    if (!timeChanged && !coveredChanged) return 'Nothing has been changed.'
    return null
  }

  return (
    <UserDataActionDialog
      title="Edit run"
      message={`${row.adventureTitle}, started ${formatDate(row.startedAt ?? row.createdAt)}. The time is what completion-time leaderboards rank on; the run's per-interval stats are not rewritten.`}
      confirmLabel="Save"
      validate={validate}
      pending={pending}
      error={error}
      onSubmit={(reason) =>
        onSubmit({
          durationSeconds: timeChanged ? (seconds as number) : undefined,
          coveredMeters: coveredChanged ? (meters as number) : undefined,
          reason,
        })
      }
      onClose={onClose}
    >
      <DurationFields label="Time" value={time} onChange={setTime} />
      <MetersField label="Covered distance" value={covered} onChange={setCovered} />
    </UserDataActionDialog>
  )
}

export function EditStepEventDialog({
  row,
  pending,
  error,
  onSubmit,
  onClose,
}: EditDialogProps<AdminUserStepEventDto, AdminEditStepEventRequest>) {
  const [steps, setSteps] = useState(String(row.steps))
  const count = parseWhole(steps)

  const validate = () => {
    if (Number.isNaN(count) || count < 1 || count > MAX_STEP_EVENT_STEPS)
      return `Steps must be a whole number from 1 to ${MAX_STEP_EVENT_STEPS.toLocaleString()}.`
    if (count === row.steps) return 'Nothing has been changed.'
    return null
  }

  return (
    <UserDataActionDialog
      title="Edit step record"
      message={`${row.title || 'Step record'}, ${formatDate(row.occurredAt)}. Lifetime steps and step achievements are recalculated.`}
      confirmLabel="Save"
      validate={validate}
      pending={pending}
      error={error}
      onSubmit={(reason) => onSubmit({ steps: count, reason })}
      onClose={onClose}
    >
      <TextField
        label="Steps"
        type="number"
        size="small"
        value={steps}
        onChange={(e) => setSteps(e.target.value)}
        inputProps={{ min: 1, max: MAX_STEP_EVENT_STEPS, step: 1 }}
        helperText={`Currently ${row.steps.toLocaleString()}; 1 to ${MAX_STEP_EVENT_STEPS.toLocaleString()}`}
        fullWidth
        autoFocus
        sx={{ mt: 2 }}
      />
    </UserDataActionDialog>
  )
}

export function EditTrailDialog({
  row,
  pending,
  error,
  onSubmit,
  onClose,
}: EditDialogProps<AdminUserTrailDto, AdminEditTrailRequest>) {
  const [distance, setDistance] = useState(() => metersText(row.distanceMeters))
  const meters = parseMeters(distance)

  const validate = () => {
    if (meters === null || Number.isNaN(meters) || meters <= 0) return 'The distance must be a number of metres above zero.'
    if (!metersChanged(meters, row.distanceMeters)) return 'Nothing has been changed.'
    return null
  }

  return (
    <UserDataActionDialog
      title="Edit trail distance"
      message={`${row.name}, recorded ${formatDate(row.recordedAt)}. Currently ${formatDistance(row.distanceMeters)}.`}
      confirmLabel="Save"
      validate={validate}
      pending={pending}
      error={error}
      onSubmit={(reason) => onSubmit({ distanceMeters: meters as number, reason })}
      onClose={onClose}
    >
      <MetersField label="Distance" value={distance} onChange={setDistance} />
    </UserDataActionDialog>
  )
}

export function EditWalkDialog({
  row,
  pending,
  error,
  onSubmit,
  onClose,
}: EditDialogProps<AdminUserWalkDto, AdminEditWalkRequest>) {
  const [time, setTime] = useState(() => splitSeconds(row.elapsedMs / 1000))
  const [distance, setDistance] = useState(() => metersText(row.distanceMeters))

  const seconds = joinSeconds(time)
  const meters = parseMeters(distance)
  // Compared at whole seconds, which is all the inputs can express.
  const timeChanged = seconds !== null && seconds !== Math.round(row.elapsedMs / 1000)
  const distanceChanged = metersChanged(meters, row.distanceMeters)

  const validate = () => {
    if (seconds !== null && (Number.isNaN(seconds) || seconds <= 0))
      return 'The time must be whole hours, minutes and seconds, and more than zero.'
    if (Number.isNaN(meters)) return 'The distance must be a number of metres, zero or more.'
    if (!timeChanged && !distanceChanged) return 'Nothing has been changed.'
    return null
  }

  return (
    <UserDataActionDialog
      title="Edit walk"
      message={`${row.trailName}, started ${formatDate(row.startedAt)}.`}
      confirmLabel="Save"
      validate={validate}
      pending={pending}
      error={error}
      onSubmit={(reason) =>
        onSubmit({
          elapsedMs: timeChanged ? (seconds as number) * 1000 : undefined,
          distanceMeters: distanceChanged ? (meters as number) : undefined,
          reason,
        })
      }
      onClose={onClose}
    >
      <DurationFields label="Elapsed time" value={time} onChange={setTime} />
      <MetersField label="Distance" value={distance} onChange={setDistance} />
    </UserDataActionDialog>
  )
}
